import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attachHereMap, detachHereMap } from '../lib/hereMap';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { TakeittoLogo } from '../components/TakeittoLogo';
import { 
  Package, Truck, MapPin, Clock, DollarSign, Weight, 
  Zap, RefreshCw, LogOut, AlertTriangle,
  CheckCircle, AlertCircle, Navigation, User, Filter, Route, Share2, Link, Settings, Camera, Plus, X, DownloadCloud, Search, ChevronDown, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || '';
const HERE_API_KEY = process.env.REACT_APP_HERE_API_KEY;

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    in_transit: { label: 'In Transit', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    delayed: { label: 'Delayed', className: 'bg-red-100 text-red-700 border-red-200' },
    upcoming: { label: 'Upcoming', className: 'bg-purple-100 text-purple-700 border-purple-200' }
  };
  
  const { label, className } = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${className}`}>
      {label}
    </span>
  );
};

// Order card component
const OrderCard = ({ order, onSelect, isSelected, drivers, onAssign, onStatusChange, onCopyTrackingLink }) => {
  const assignedDriver = drivers.find(d => d.id === order.assigned_driver_id);
  
  return (
    <div 
      className={`order-card p-3 bg-white rounded-lg cursor-pointer border-l-4 ${
        isSelected ? 'ring-2 ring-indigo-500' : ''
      }`}
      style={{
        borderLeftColor: {
          pending: '#F59E0B',
          assigned: '#3B82F6',
          in_transit: '#6366F1',
          delivered: '#10B981',
          delayed: '#EF4444',
          upcoming: '#A855F7'
        }[order.status] || '#6B7280',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
      }}
      onClick={() => onSelect(order)}
      data-testid={`order-card-${order.id}`}
    >
      <div className="flex justify-between items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="mono text-xs text-gray-500">{order.id.slice(0, 8)}</p>
          <h4 className="font-semibold text-gray-900 text-sm truncate">{order.customer_name}</h4>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={(e) => { e.stopPropagation(); onCopyTrackingLink(order.id); }}
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-indigo-600"
            title="Copy tracking link"
          >
            <Link className="w-3.5 h-3.5" />
          </button>
          <StatusBadge status={order.status} />
        </div>
      </div>
      
      <p className="text-xs text-gray-600 truncate mb-2">{order.delivery_address}</p>

      {order.items_summary && (
        <p className="text-xs text-gray-500 truncate mb-2" title={order.items_summary} data-testid={`order-items-${order.id}`}>
          <Package className="w-3 h-3 inline mr-1" />{order.items_summary}
        </p>
      )}
      {order.source === 'henrys' && (
        <span className="inline-block mb-2 px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200">
          From Henry's{order.status === 'upcoming' ? ' · awaiting payment' : ''}
        </span>
      )}
      
      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-2">
        <span className="flex items-center gap-1">
          <DollarSign className="w-3 h-3" />
          ${order.order_value.toFixed(0)}
        </span>
        <span className="flex items-center gap-1">
          <Weight className="w-3 h-3" />
          {order.total_weight}kg
        </span>
        <span className="flex items-center gap-1">
          <Navigation className="w-3 h-3" />
          {order.distance_from_dispatch}km
        </span>
      </div>
      
      {/* ETA Display for assigned/in_transit orders */}
      {order.eta_minutes && ['assigned', 'in_transit'].includes(order.status) && (
        <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded text-xs mb-2">
          <Clock className="w-3 h-3 text-indigo-600" />
          <span className="text-indigo-700 font-medium">
            ETA: {order.eta_minutes} min ({order.distance_km} km)
          </span>
        </div>
      )}
      
      {order.delivery_time_window && (
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
          <Clock className="w-3 h-3" />
          {order.delivery_time_window}
        </div>
      )}
      
      {assignedDriver && (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded text-xs mb-2">
          <Truck className="w-3 h-3 text-blue-600 shrink-0" />
          <span className="text-blue-800 truncate">{assignedDriver.name}</span>
          {order.assigned_at && (
            <span className="text-blue-500 ml-auto whitespace-nowrap">
              {new Date(order.assigned_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}

      {/* Received timestamp */}
      <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-1" data-testid={`order-received-${order.id}`}>
        <Clock className="w-3 h-3" />
        Received {order.created_at ? new Date(order.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
      </div>
      
      {order.status === 'pending' && (
        <Select onValueChange={(driverId) => onAssign(order.id, driverId)}>
          <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`assign-select-${order.id}`}>
            <SelectValue placeholder="Assign to driver" />
          </SelectTrigger>
          <SelectContent>
            {drivers.filter(d => d.available && (d.max_capacity - d.current_load) >= order.total_weight).map(driver => (
              <SelectItem key={driver.id} value={driver.id}>
                {driver.name} ({(driver.max_capacity - driver.current_load).toFixed(1)}kg)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {order.status === 'assigned' && (
        <Button 
          size="sm" 
          className="mt-1 w-full h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
          onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, 'in_transit'); }}
          data-testid={`start-delivery-${order.id}`}
        >
          Start Delivery
        </Button>
      )}
      
      {order.status === 'in_transit' && (
        <Button 
          size="sm" 
          className="mt-1 w-full h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
          onClick={(e) => { e.stopPropagation(); onStatusChange(order.id, 'delivered'); }}
          data-testid={`mark-delivered-${order.id}`}
        >
          Mark Delivered
        </Button>
      )}
      
      {/* Delivery Photo Proof for delivered orders */}
      {order.status === 'delivered' && order.delivery_photo_path && (
        <div className="mt-2 p-2 bg-emerald-50 rounded border border-emerald-100">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="w-3 h-3 text-emerald-600" />
            <span className="text-xs text-emerald-700 font-medium">Delivery Proof</span>
          </div>
          <img 
            src={`${API}/orders/${order.id}/delivery-photo`}
            alt="Delivery proof"
            className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`${API}/orders/${order.id}/delivery-photo`, '_blank');
            }}
            data-testid={`delivery-photo-${order.id}`}
          />
        </div>
      )}
    </div>
  );
};

// Driver card component
const DriverCard = ({ driver, isSelected, onSelect, isTracked, onTrack }) => {
  const capacityPercent = (driver.current_load / driver.max_capacity) * 100;
  
  return (
    <div 
      className={`p-3 bg-white rounded-lg cursor-pointer ${
        isSelected ? 'ring-2 ring-indigo-500' : ''
      } ${isTracked ? 'bg-indigo-50 border border-indigo-200' : ''}`}
      style={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}
      onClick={() => onSelect(driver)}
      data-testid={`driver-card-${driver.id}`}
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-shrink-0">
          <img 
            src={driver.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.name)}&background=6366F1&color=fff`}
            alt={driver.name}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
            driver.available ? 'bg-emerald-500' : 'bg-gray-400'
          }`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm truncate">{driver.name}</h4>
          <p className="text-xs text-gray-500 truncate">{driver.phone}</p>
          {driver.city && (
            <p className="text-xs text-indigo-600 font-medium">{driver.city}</p>
          )}
        </div>
        
        <Button
          size="sm"
          variant={isTracked ? "default" : "outline"}
          className={`shrink-0 h-7 w-7 p-0 ${isTracked ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
          onClick={(e) => { e.stopPropagation(); onTrack(); }}
          data-testid={`track-driver-${driver.id}`}
        >
          <Navigation className={`w-3 h-3 ${isTracked ? 'animate-pulse' : ''}`} />
        </Button>
      </div>
      
      <div className="mt-2">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Capacity</span>
          <span>{driver.current_load.toFixed(1)} / {driver.max_capacity}kg</span>
        </div>
        <Progress value={capacityPercent} className="h-1.5" />
      </div>
      
      {isTracked && (
        <div className="mt-2 p-1.5 bg-indigo-100 rounded">
          <p className="text-xs text-indigo-800 font-medium flex items-center gap-1">
            <Navigation className="w-3 h-3 animate-pulse" />
            Live Tracking
          </p>
        </div>
      )}
    </div>
  );
};

// Flexible Polyline Decoder for HERE Maps
const decodeFlexiblePolyline = (encoded) => {
  if (!encoded) return [];
  
  const DECODING_TABLE = [
    62, -1, -1, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1, -1, -1, -1, -1,
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, -1, -1, -1, -1, 63, -1, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35,
    36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51
  ];
  
  const decodeChar = (char) => {
    const charCode = char.charCodeAt(0);
    return DECODING_TABLE[charCode - 45] || -1;
  };
  
  const decodeUnsignedValue = (encoded, startIndex) => {
    let result = 0;
    let shift = 0;
    let index = startIndex;
    
    while (index < encoded.length) {
      const value = decodeChar(encoded[index]);
      result |= (value & 0x1F) << shift;
      if ((value & 0x20) === 0) {
        return { value: result, nextIndex: index + 1 };
      }
      shift += 5;
      index++;
    }
    return { value: result, nextIndex: index };
  };
  
  const decodeSignedValue = (encoded, startIndex) => {
    const { value, nextIndex } = decodeUnsignedValue(encoded, startIndex);
    const signedValue = (value & 1) ? ~(value >> 1) : (value >> 1);
    return { value: signedValue, nextIndex };
  };
  
  try {
    // Skip header (first 2 chars contain precision info)
    let index = 2;
    
    // Get precision from header
    const { value: header } = decodeUnsignedValue(encoded, 0);
    const precision = Math.pow(10, -(header & 15));
    
    const coordinates = [];
    let lat = 0;
    let lng = 0;
    
    // Start after header
    index = 2;
    
    while (index < encoded.length) {
      const latResult = decodeSignedValue(encoded, index);
      lat += latResult.value;
      index = latResult.nextIndex;
      
      if (index >= encoded.length) break;
      
      const lngResult = decodeSignedValue(encoded, index);
      lng += lngResult.value;
      index = lngResult.nextIndex;
      
      coordinates.push({
        lat: lat * precision,
        lng: lng * precision
      });
    }
    
    return coordinates;
  } catch (e) {
    console.error('Polyline decode error:', e);
    return [];
  }
};

// HERE Map Component
// Map viewport helpers: keep the map focused on Australia so a single
// mis-geocoded point can't zoom the view out to the whole world
const AUSTRALIA_BOUNDS = { north: -10.0, south: -44.0, west: 112.5, east: 154.0 };
const CITY_FIT_RADIUS_KM = 150;
const MAP_PADDING = 60;

const isInAustralia = (lat, lng) =>
  lat <= AUSTRALIA_BOUNDS.north && lat >= AUSTRALIA_BOUNDS.south &&
  lng >= AUSTRALIA_BOUNDS.west && lng <= AUSTRALIA_BOUNDS.east;

const distanceKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Fit the map to the given points; use the fallback view when there are no points
const fitMapToPoints = (map, points, fallback) => {
  if (points.length === 0) {
    if (fallback?.bounds) {
      const b = fallback.bounds;
      map.getViewModel().setLookAtData({ bounds: new window.H.geo.Rect(b.north, b.west, b.south, b.east) }, true);
    } else if (fallback?.center) {
      map.setCenter(fallback.center, true);
      map.setZoom(fallback.zoom || 11, true);
    }
    return;
  }
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  points.forEach(p => {
    minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
  });
  // A single point (or points on top of each other) has no area to fit, so centre on it instead
  if (maxLat - minLat < 0.005 && maxLng - minLng < 0.005) {
    map.setCenter({ lat: points[0].lat, lng: points[0].lng }, true);
    map.setZoom(13, true);
    return;
  }
  map.getViewModel().setLookAtData({ bounds: new window.H.geo.Rect(maxLat, minLng, minLat, maxLng) }, true);
};

const HereMap = ({ orders, drivers, dispatchLocations, selectedOrder, selectedDriver, trackedDriver, cityFilter, trafficData, trafficIncidents, routes, showRoutes }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const platformRef = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const behaviorRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);
  
  useEffect(() => {
    if (mapInstanceRef.current) return;
    
    const initMap = () => {
      if (!mapRef.current) return;
      if (typeof window.H === 'undefined') {
        setMapError('HERE Maps SDK not loaded. Please refresh.');
        return;
      }
      
      try {
        const map = attachHereMap('dispatch', mapRef.current, (element) => {
          const platform = new window.H.service.Platform({ apikey: HERE_API_KEY });
          platformRef.current = platform;
          const defaultLayers = platform.createDefaultLayers();
          
          const newMap = new window.H.Map(
            element,
            defaultLayers.vector.normal.map,
            { center: { lat: -33.8688, lng: 151.2093 }, zoom: 11, pixelRatio: window.devicePixelRatio || 1 }
          );
          
          behaviorRef.current = new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(newMap));
          window.H.ui.UI.createDefault(newMap, defaultLayers);
          
          // Keep markers clear of the map edges and overlays when fitting the view
          newMap.getViewPort().setPadding(MAP_PADDING, MAP_PADDING, MAP_PADDING, MAP_PADDING);
          return newMap;
        });
        mapInstanceRef.current = map;
        setMapReady(true);
      } catch (err) {
        setMapError(`Map failed: ${err.message}`);
      }
    };
    
    const timeoutId = setTimeout(initMap, 500);
    return () => {
      clearTimeout(timeoutId);
      if (mapInstanceRef.current) detachHereMap('dispatch');
      mapInstanceRef.current = null;
    };
  }, []);
  
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    
    markersRef.current.forEach(marker => map.removeObject(marker));
    markersRef.current = [];
    
    // Add dispatch markers
    dispatchLocations.forEach(dispatch => {
      if (!dispatch.latitude || !dispatch.longitude) return;
      if (cityFilter !== 'all' && !dispatch.name.includes(cityFilter)) return;
      
      const icon = new window.H.map.Icon(
        `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#1F2937" stroke="white" stroke-width="3"/>
          <path d="M10 16h20v8a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V16Z" fill="none" stroke="white" stroke-width="2"/>
          <path d="m10 16 2-4h16l2 4" fill="none" stroke="white" stroke-width="2"/>
        </svg>`,
        { size: { w: 40, h: 40 }, anchor: { x: 20, y: 20 } }
      );
      const marker = new window.H.map.Marker({ lat: dispatch.latitude, lng: dispatch.longitude }, { icon });
      map.addObject(marker);
      markersRef.current.push(marker);
    });
    
    // Add order markers
    orders.forEach(order => {
      if (!order.latitude || !order.longitude) return;
      const colors = { pending: '#F59E0B', assigned: '#3B82F6', in_transit: '#6366F1', delivered: '#10B981', delayed: '#EF4444' };
      const color = colors[order.status] || '#6B7280';
      
      const icon = new window.H.map.Icon(
        `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="11" fill="white" stroke="${color}" stroke-width="3"/>
          <rect x="9" y="9" width="10" height="10" rx="2" fill="${color}"/>
        </svg>`,
        { size: { w: 28, h: 28 }, anchor: { x: 14, y: 14 } }
      );
      const marker = new window.H.map.Marker({ lat: order.latitude, lng: order.longitude }, { icon });
      map.addObject(marker);
      markersRef.current.push(marker);
    });
    
    // Add driver markers
    drivers.forEach(driver => {
      if (!driver.latitude || !driver.longitude) return;
      const isTracked = trackedDriver?.id === driver.id;
      const size = isTracked ? 44 : 36;
      
      const icon = new window.H.map.Icon(
        `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
          ${isTracked ? `<circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="none" stroke="#A5B4FC" stroke-width="3" opacity="0.6"/>` : ''}
          <circle cx="${size/2}" cy="${size/2}" r="${isTracked ? 16 : 14}" fill="#6366F1" stroke="white" stroke-width="3"/>
          <rect x="${size/2-10}" y="${size/2-5}" width="20" height="10" rx="2" fill="none" stroke="white" stroke-width="2"/>
          <circle cx="${size/2-6}" cy="${size/2+5}" r="2" fill="none" stroke="white" stroke-width="1.5"/>
          <circle cx="${size/2+6}" cy="${size/2+5}" r="2" fill="none" stroke="white" stroke-width="1.5"/>
        </svg>`,
        { size: { w: size, h: size }, anchor: { x: size/2, y: size/2 } }
      );
      const marker = new window.H.map.Marker({ lat: driver.latitude, lng: driver.longitude }, { icon });
      map.addObject(marker);
      markersRef.current.push(marker);
    });
    
    // Fit bounds - All Cities shows the whole of Australia; a single city fits the markers
    // near that city's dispatch point. Points outside Australia never move the view.
    const cityDispatch = cityFilter !== 'all'
      ? dispatchLocations.find(d => d.name.includes(cityFilter))
      : null;
    if (!cityDispatch) {
      fitMapToPoints(map, [], { bounds: AUSTRALIA_BOUNDS });
    } else {
      const points = markersRef.current
        .map(marker => marker.getGeometry())
        .filter(pos => pos && isInAustralia(pos.lat, pos.lng) &&
          distanceKm(pos.lat, pos.lng, cityDispatch.latitude, cityDispatch.longitude) <= CITY_FIT_RADIUS_KM);
      fitMapToPoints(map, points, { center: { lat: cityDispatch.latitude, lng: cityDispatch.longitude }, zoom: 11 });
    }
  }, [orders, drivers, dispatchLocations, selectedOrder, selectedDriver, trackedDriver, cityFilter, trafficIncidents, mapReady]);
  
  // Handle route polylines
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;
    
    // Clear existing polylines
    polylinesRef.current.forEach(polyline => {
      try { map.removeObject(polyline); } catch (e) {}
    });
    polylinesRef.current = [];
    
    if (!showRoutes || !routes || routes.length === 0) return;
    
    // Route colors based on status
    const routeColors = {
      assigned: '#3B82F6',  // Blue
      in_transit: '#6366F1' // Indigo
    };
    
    routes.forEach((route, index) => {
      if (!route.polyline) return;
      
      try {
        // Decode the flexible polyline
        const coordinates = decodeFlexiblePolyline(route.polyline);
        
        if (coordinates.length < 2) {
          // Fallback: draw direct line if decoding fails
          const lineString = new window.H.geo.LineString();
          lineString.pushPoint(route.origin);
          lineString.pushPoint(route.destination);
          
          const polyline = new window.H.map.Polyline(lineString, {
            style: {
              strokeColor: routeColors[route.status] || '#6366F1',
              lineWidth: 4,
              lineDash: [10, 5]
            }
          });
          map.addObject(polyline);
          polylinesRef.current.push(polyline);
          return;
        }
        
        // Create line string from decoded coordinates
        const lineString = new window.H.geo.LineString();
        coordinates.forEach(coord => {
          lineString.pushPoint({ lat: coord.lat, lng: coord.lng });
        });
        
        // Create polyline with styling
        const polyline = new window.H.map.Polyline(lineString, {
          style: {
            strokeColor: routeColors[route.status] || '#6366F1',
            lineWidth: 5,
            lineCap: 'round',
            lineJoin: 'round'
          }
        });
        
        map.addObject(polyline);
        polylinesRef.current.push(polyline);
        
        // Add route outline for better visibility
        const outlineString = new window.H.geo.LineString();
        coordinates.forEach(coord => {
          outlineString.pushPoint({ lat: coord.lat, lng: coord.lng });
        });
        
        const outline = new window.H.map.Polyline(outlineString, {
          style: {
            strokeColor: 'rgba(255,255,255,0.6)',
            lineWidth: 8,
            lineCap: 'round',
            lineJoin: 'round'
          }
        });
        
        // Insert outline behind the main polyline
        map.addObject(outline);
        polylinesRef.current.push(outline);
        
      } catch (e) {
        console.error('Error rendering route:', e);
      }
    });
  }, [routes, showRoutes, mapReady]);
  
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady || !trackedDriver?.latitude || !trackedDriver?.longitude) return;
    map.setCenter({ lat: trackedDriver.latitude, lng: trackedDriver.longitude }, true);
    map.setZoom(15, true);
  }, [trackedDriver, mapReady]);
  
  if (mapError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-6">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">{mapError}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>Reload</Button>
        </div>
      </div>
    );
  }
  
  return <div ref={mapRef} className="w-full h-full" style={{ background: '#e5e5e5' }} />;
};

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dispatchLocations, setDispatchLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [trackedDriver, setTrackedDriver] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (label) =>
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  
  const [trafficData, setTrafficData] = useState(null);
  const [trafficIncidents, setTrafficIncidents] = useState(null);
  const [showTraffic, setShowTraffic] = useState(false);
  
  const [routes, setRoutes] = useState([]);
  const [showRoutes, setShowRoutes] = useState(false);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [isSyncingHenrys, setIsSyncingHenrys] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  
  // New Order Modal state
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [newOrderForm, setNewOrderForm] = useState({
    customer_name: '',
    delivery_address: '',
    order_value: '',
    total_weight: '',
    delivery_time_window: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token || !WS_URL) return;
    // Closing the socket on unmount fires onclose, which must not start a new connection
    let unmounted = false;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws/dashboard`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('Dashboard WebSocket connected');
          setWsConnected(true);
          // Start keepalive ping
          const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, 30000);
          ws.pingInterval = pingInterval;
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'driver_location') {
              // Update driver location in state
              setDrivers(prev => prev.map(d => 
                d.id === data.driver_id 
                  ? { ...d, latitude: data.latitude, longitude: data.longitude }
                  : d
              ));
            } else if (data.type === 'order_update') {
              // Update order in state
              setOrders(prev => prev.map(o => 
                o.id === data.order.id ? data.order : o
              ));
              toast.info(`Order ${data.order.id.slice(0, 6)} updated to ${data.order.status}`);
            }
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        };
        
        ws.onclose = () => {
          console.log('Dashboard WebSocket disconnected');
          setWsConnected(false);
          if (ws.pingInterval) clearInterval(ws.pingInterval);
          // Reconnect after 3 seconds
          if (!unmounted) setTimeout(connectWebSocket, 3000);
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
      } catch (e) {
        console.error('WebSocket connection failed:', e);
      }
    };
    
    connectWebSocket();
    
    return () => {
      unmounted = true;
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
    };
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const cityParam = cityFilter !== 'all' ? `?city=${cityFilter}` : '';
      const [ordersRes, driversRes, dispatchRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/orders/with-eta${cityParam}`, { headers }),
        axios.get(`${API}/personnel${cityParam}`, { headers }),
        axios.get(`${API}/dispatch-locations`, { headers }),
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/assignments/logs?limit=20`, { headers })
      ]);
      
      setOrders(ordersRes.data);
      setDrivers(driversRes.data);
      setDispatchLocations(dispatchRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      if (error.response?.status === 401) { logout(); navigate('/login'); }
    } finally {
      setIsLoading(false);
    }
  }, [token, logout, navigate, cityFilter]);

  const fetchRoutes = useCallback(async () => {
    if (!showRoutes) {
      setRoutes([]);
      return;
    }
    
    setIsLoadingRoutes(true);
    try {
      const cityParam = cityFilter !== 'all' ? `?city=${cityFilter}` : '';
      const res = await axios.get(`${API}/routes/all-active${cityParam}`, { headers });
      setRoutes(res.data.routes || []);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
      toast.error('Failed to load routes');
    } finally {
      setIsLoadingRoutes(false);
    }
  }, [showRoutes, cityFilter, token]);

  const fetchTrafficData = useCallback(async () => {
    if (!showTraffic || !dispatchLocations.length) return;
    try {
      const dispatch = dispatchLocations.find(d => cityFilter === 'all' ? d.is_primary : d.name.includes(cityFilter)) || dispatchLocations[0];
      if (!dispatch) return;
      const bbox = { south: dispatch.latitude - 0.1, west: dispatch.longitude - 0.1, north: dispatch.latitude + 0.1, east: dispatch.longitude + 0.1 };
      const [flowRes, incidentsRes] = await Promise.all([
        axios.post(`${API}/here/traffic/flow`, bbox, { headers }),
        axios.post(`${API}/here/traffic/incidents`, bbox, { headers })
      ]);
      setTrafficData(flowRes.data);
      setTrafficIncidents(incidentsRes.data);
    } catch (error) {}
  }, [showTraffic, dispatchLocations, cityFilter, token]);

  useEffect(() => { fetchData(); const interval = setInterval(fetchData, 30000); return () => clearInterval(interval); }, [fetchData]);
  useEffect(() => { fetchTrafficData(); }, [fetchTrafficData]);
  useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

  const handleAutoAssign = async () => {
    setIsAutoAssigning(true);
    try {
      const response = await axios.post(`${API}/assignments/auto-assign`, {}, { headers });
      toast.success(`${response.data.assignments_made} orders assigned!`);
      fetchData();
    } catch { toast.error('Auto-assignment failed'); }
    finally { setIsAutoAssigning(false); }
  };

  const handleHenrysSync = async () => {
    setIsSyncingHenrys(true);
    try {
      const res = await axios.post(`${API}/integration/henrys/sync`, {}, { headers });
      const paid = res.data.results?.paid || {};
      const ready = res.data.results?.ready || {};
      const newCount = (paid.created || 0) + (ready.created || 0);
      const updCount = (paid.updated || 0) + (ready.updated || 0);
      toast.success(
        `Henry's sync: ${newCount} new, ${updCount} updated — ${paid.created || 0} ready-to-dispatch, ${ready.created || 0} upcoming`
      );
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Could not reach Henry's API";
      toast.error(`Henry's sync failed: ${detail}`);
    } finally { setIsSyncingHenrys(false); }
  };

  const handleManualAssign = async (orderId, driverId) => {
    try {
      await axios.post(`${API}/assignments/assign-order/${orderId}/${driverId}`, {}, { headers });
      toast.success('Order assigned');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.post(`${API}/orders/${orderId}/status/${newStatus}`, {}, { headers });
      toast.success(`Status updated`);
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const copyTrackingLink = (orderId) => {
    const trackingCode = orderId.slice(0, 8);
    const url = `${window.location.origin}/track/${trackingCode}`;
    navigator.clipboard.writeText(url);
    toast.success('Tracking link copied!');
  };

  // Create new order handler
  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    if (!newOrderForm.customer_name.trim() || !newOrderForm.delivery_address.trim()) {
      toast.error('Customer name and delivery address are required');
      return;
    }
    
    if (!newOrderForm.order_value || parseFloat(newOrderForm.order_value) <= 0) {
      toast.error('Please enter a valid order value');
      return;
    }
    
    if (!newOrderForm.total_weight || parseFloat(newOrderForm.total_weight) <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }
    
    setIsCreatingOrder(true);
    try {
      const orderData = {
        customer_name: newOrderForm.customer_name.trim(),
        delivery_address: newOrderForm.delivery_address.trim(),
        order_value: parseFloat(newOrderForm.order_value),
        total_weight: parseFloat(newOrderForm.total_weight),
        delivery_time_window: newOrderForm.delivery_time_window.trim() || null
      };
      
      await axios.post(`${API}/orders`, orderData, { headers });
      toast.success('Order created successfully!');
      
      // Reset form and close modal
      setNewOrderForm({
        customer_name: '',
        delivery_address: '',
        order_value: '',
        total_weight: '',
        delivery_time_window: ''
      });
      setShowNewOrderModal(false);
      
      // Refresh orders
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const driverById = React.useMemo(() => {
    const m = {};
    (drivers || []).forEach(d => { m[d.id] = d; });
    return m;
  }, [drivers]);

  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    if (assignmentFilter === 'assigned' && !order.assigned_driver_id) return false;
    if (assignmentFilter === 'unassigned' && order.assigned_driver_id) return false;
    const q = orderSearch.trim().toLowerCase();
    if (q) {
      const drv = order.assigned_driver_id ? driverById[order.assigned_driver_id] : null;
      const hay = [
        order.id, order.customer_name, order.delivery_address, order.customer_phone,
        order.customer_email, order.items_summary, order.henrys_order_id, order.city,
        drv?.name
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Newest first by received (created) time
  const sortedOrders = [...filteredOrders].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );

  // Group by the day the order was received
  const dateGroupLabel = (iso) => {
    if (!iso) return 'Unknown date';
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date(); yest.setDate(today.getDate() - 1);
    const sameDay = (x, y) => x.toDateString() === y.toDateString();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yest)) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  const orderGroups = [];
  sortedOrders.forEach(o => {
    const label = dateGroupLabel(o.created_at);
    let g = orderGroups.find(x => x.label === label);
    if (!g) { g = { label, orders: [] }; orderGroups.push(g); }
    g.orders.push(o);
  });

  const filteredDrivers = (drivers || []).filter(d => {
    const q = driverSearch.trim().toLowerCase();
    if (!q) return true;
    return [d.name, d.email, d.phone, d.city, d.vehicle_make_model].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <TakeittoLogo size="sm" />
            
            <Select value={cityFilter} onValueChange={setCityFilter}>
              <SelectTrigger className="w-[130px] sm:w-[150px] h-9" data-testid="city-filter">
                <MapPin className="w-4 h-4 mr-1 text-indigo-600" />
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                <SelectItem value="Sydney">Sydney</SelectItem>
                <SelectItem value="Melbourne">Melbourne</SelectItem>
                <SelectItem value="Brisbane">Brisbane</SelectItem>
                <SelectItem value="Perth">Perth</SelectItem>
                <SelectItem value="Adelaide">Adelaide</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <Button 
              onClick={() => setShowNewOrderModal(true)} 
              size="sm" 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="new-order-btn"
              title="New Order"
            >
              <Plus className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">New Order</span>
            </Button>
            <Button 
              variant={showRoutes ? "default" : "outline"} 
              size="sm" 
              onClick={() => setShowRoutes(!showRoutes)} 
              disabled={isLoadingRoutes}
              className={showRoutes ? 'bg-blue-600 hover:bg-blue-700' : ''}
              data-testid="routes-toggle"
              title="Routes"
            >
              <Route className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">{isLoadingRoutes ? '...' : 'Routes'}</span>
            </Button>
            <Button variant={showTraffic ? "default" : "outline"} size="sm" onClick={() => setShowTraffic(!showTraffic)} className={showTraffic ? 'bg-red-600 hover:bg-red-700' : ''} title="Traffic">
              <AlertTriangle className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">Traffic</span>
            </Button>
            <Button onClick={handleAutoAssign} disabled={isAutoAssigning} size="sm" className="bg-indigo-600 hover:bg-indigo-700" title="Auto-Assign">
              <Zap className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">{isAutoAssigning ? '...' : 'Auto-Assign'}</span>
            </Button>
            <Button onClick={handleHenrysSync} disabled={isSyncingHenrys} size="sm" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" title="Sync paid orders from Henry's" data-testid="henrys-sync-btn">
              <DownloadCloud className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">{isSyncingHenrys ? 'Syncing...' : "Sync from Henry's"}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')} title="Admin Console">
              <Settings className="w-4 h-4 lg:mr-1" /><span className="hidden lg:inline">Admin</span>
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} title="Refresh"><RefreshCw className="w-4 h-4" /></Button>
            {/* WebSocket Status */}
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-gray-300'}`} title={wsConnected ? 'Real-time connected' : 'Connecting...'} />
            <div className="flex items-center gap-2 pl-2 border-l">
              <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-xs hidden sm:block">
                <p className="font-medium text-gray-900">{user?.name}</p>
                <p className="text-gray-500 capitalize">{user?.role}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }}><LogOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      {stats && (
        <div className="px-3 sm:px-4 py-2 bg-white border-b border-gray-200 flex flex-wrap gap-x-4 gap-y-1 text-sm shrink-0">
          <span className="flex items-center gap-1"><Package className="w-4 h-4 text-gray-400" />Orders: <b>{stats.orders.total}</b></span>
          <span className="flex items-center gap-1"><AlertCircle className="w-4 h-4 text-amber-500" />Pending: <b className="text-amber-600">{stats.orders.pending}</b></span>
          <span className="flex items-center gap-1"><Navigation className="w-4 h-4 text-indigo-500" />Transit: <b className="text-indigo-600">{stats.orders.in_transit}</b></span>
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-500" />Done: <b className="text-emerald-600">{stats.orders.delivered}</b></span>
          <span className="flex items-center gap-1 ml-auto"><Truck className="w-4 h-4 text-indigo-500" />Drivers: <b className="text-indigo-600">{stats.drivers.available}/{stats.drivers.total}</b></span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {/* Sidebar - below the map on phones, beside it from tablet width */}
        <div className="order-2 md:order-1 flex-1 min-h-0 md:flex-none md:w-[340px] lg:w-[380px] md:h-full border-t md:border-t-0 md:border-r border-gray-200 bg-white flex flex-col">
          <Tabs defaultValue="orders" className="flex-1 flex flex-col h-full overflow-hidden">
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-10 px-2 shrink-0">
              <TabsTrigger value="orders" className="text-xs data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"><Package className="w-3 h-3 mr-1" />Orders</TabsTrigger>
              <TabsTrigger value="drivers" className="text-xs data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"><Truck className="w-3 h-3 mr-1" />Drivers</TabsTrigger>
              <TabsTrigger value="logs" className="text-xs data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700"><Clock className="w-3 h-3 mr-1" />Logs</TabsTrigger>
            </TabsList>
            
            <TabsContent value="orders" className="flex-1 m-0 overflow-hidden flex flex-col">
              {/* Filters */}
              <div className="p-2 border-b border-gray-100 space-y-2 shrink-0 bg-white">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    placeholder="Search order #, customer, address, driver..."
                    className="w-full h-8 pl-7 pr-7 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="order-search-input"
                  />
                  {orderSearch && (
                    <button onClick={() => setOrderSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="order-search-clear">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex gap-1">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid="status-filter"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="delayed">Delayed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                    <SelectTrigger className="flex-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500" data-testid="order-count">{sortedOrders.length} orders</p>
                  {orderGroups.length > 0 && (
                    <button
                      onClick={() => {
                        const anyExpanded = orderGroups.some(g => !collapsedGroups[g.label]);
                        const next = {};
                        if (anyExpanded) orderGroups.forEach(g => { next[g.label] = true; });
                        setCollapsedGroups(next);
                      }}
                      className="text-xs text-indigo-600 hover:underline flex items-center gap-1"
                      data-testid="toggle-all-groups"
                    >
                      {orderGroups.some(g => !collapsedGroups[g.label])
                        ? (<><ChevronRight className="w-3 h-3" /> Collapse all</>)
                        : (<><ChevronDown className="w-3 h-3" /> Expand all</>)}
                    </button>
                  )}
                </div>
              </div>
              {/* Scrollable Orders List - grouped by received date, newest first */}
              <div className="flex-1 overflow-y-auto p-2 space-y-3">
                {orderGroups.map(group => {
                  const collapsed = orderSearch.trim() ? false : !!collapsedGroups[group.label];
                  return (
                  <div key={group.label} className="space-y-2">
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className="w-full sticky top-0 z-10 flex items-center gap-2 bg-slate-50 py-1 px-1 -mx-1 rounded hover:bg-slate-100 transition-colors"
                      data-testid={`order-group-toggle-${group.label}`}
                    >
                      {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
                      <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide" data-testid={`order-group-${group.label}`}>{group.label}</span>
                      <span className="text-[10px] text-gray-400">{group.orders.length}</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </button>
                    {!collapsed && group.orders.map(order => (
                      <OrderCard key={order.id} order={order} drivers={drivers} isSelected={selectedOrder?.id === order.id} onSelect={setSelectedOrder} onAssign={handleManualAssign} onStatusChange={handleStatusChange} onCopyTrackingLink={copyTrackingLink} />
                    ))}
                  </div>
                  );
                })}
                {sortedOrders.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No orders found</p>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="drivers" className="flex-1 m-0 overflow-y-auto">
              <div className="p-2 space-y-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    placeholder="Search drivers..."
                    className="w-full h-8 pl-7 pr-7 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    data-testid="driver-search-input"
                  />
                  {driverSearch && (
                    <button onClick={() => setDriverSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {filteredDrivers.map(driver => (
                  <DriverCard key={driver.id} driver={driver} isSelected={selectedDriver?.id === driver.id} onSelect={setSelectedDriver} isTracked={trackedDriver?.id === driver.id} onTrack={() => setTrackedDriver(trackedDriver?.id === driver.id ? null : driver)} />
                ))}
                {filteredDrivers.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No drivers found</p>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="logs" className="flex-1 m-0 overflow-y-auto">
              <div className="p-2 space-y-1">
                {logs.map(log => (
                  <div key={log.id} className="p-2 bg-gray-50 rounded text-xs">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Badge variant="outline" className="text-[10px] capitalize">{log.action}</Badge>
                      <span className="mono text-[10px] text-gray-400">{new Date(log.created_at).toLocaleTimeString()}</span>
                    </div>
                    <p className="text-gray-600">{log.details}</p>
                  </div>
                ))}
                {logs.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No logs found</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Map */}
        <div className="order-1 md:order-2 relative h-[40dvh] shrink-0 md:h-auto md:flex-1 md:shrink min-w-0">
          <HereMap orders={filteredOrders} drivers={drivers} dispatchLocations={dispatchLocations} selectedOrder={selectedOrder} selectedDriver={selectedDriver} trackedDriver={trackedDriver} cityFilter={cityFilter} trafficData={trafficData} trafficIncidents={trafficIncidents} routes={routes} showRoutes={showRoutes} />
          
          {/* Legend */}
          <div className="hidden sm:block absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
            <h4 className="font-semibold text-xs mb-2 flex items-center gap-1"><Route className="w-3 h-3 text-indigo-600" />Legend</h4>
            <div className="space-y-1 text-[10px]">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-gray-800" /><span>Dispatch</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-indigo-600" /><span>Driver</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-amber-500 bg-white" /><span>Pending</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-blue-500 bg-white" /><span>Assigned</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-indigo-500 bg-white" /><span>In Transit</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full border-2 border-emerald-500 bg-white" /><span>Delivered</span></div>
              {showRoutes && (
                <>
                  <div className="border-t border-gray-200 pt-1 mt-1"></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-0.5 bg-blue-500" /><span>Assigned Route</span></div>
                  <div className="flex items-center gap-1"><div className="w-4 h-0.5 bg-indigo-600" /><span>In Transit Route</span></div>
                </>
              )}
            </div>
          </div>
          
          {/* Tracking Panel */}
          {trackedDriver && (
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-white rounded-lg shadow-lg p-3 z-[1000] w-[220px] sm:w-[240px]">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-xs flex items-center gap-1"><Navigation className="w-3 h-3 text-indigo-600 animate-pulse" />Live Tracking</h4>
                <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => setTrackedDriver(null)}>×</Button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <img src={trackedDriver.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(trackedDriver.name)}&background=6366F1&color=fff`} alt={trackedDriver.name} className="w-8 h-8 rounded-full" />
                <div>
                  <p className="font-medium text-xs">{trackedDriver.name}</p>
                  <p className="text-[10px] text-gray-500">{trackedDriver.phone}</p>
                </div>
              </div>
              <div className="space-y-1 text-[10px]">
                <div className="flex justify-between"><span className="text-gray-500">City:</span><span className="font-medium text-indigo-600">{trackedDriver.city || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Status:</span><span className={`font-medium ${trackedDriver.available ? 'text-emerald-600' : 'text-gray-500'}`}>{trackedDriver.available ? 'Available' : 'Busy'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Load:</span><span>{trackedDriver.current_load?.toFixed(1)}/{trackedDriver.max_capacity}kg</span></div>
                <div className="pt-1 border-t mt-1">
                  <p className="text-gray-500">Coords:</p>
                  <p className="mono">{trackedDriver.latitude?.toFixed(5)}, {trackedDriver.longitude?.toFixed(5)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* New Order Modal */}
      {showNewOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-order-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl max-h-[90dvh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                Create New Order
              </h3>
              <button 
                onClick={() => setShowNewOrderModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="close-new-order-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Form */}
            <form onSubmit={handleCreateOrder} className="p-4 space-y-4">
              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newOrderForm.customer_name}
                  onChange={(e) => setNewOrderForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                  data-testid="new-order-customer-name"
                  required
                />
              </div>
              
              {/* Delivery Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newOrderForm.delivery_address}
                  onChange={(e) => setNewOrderForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                  placeholder="123 Main St, Sydney NSW 2000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                  data-testid="new-order-address"
                  required
                />
              </div>
              
              {/* Order Value & Weight Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Value ($) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newOrderForm.order_value}
                    onChange={(e) => setNewOrderForm(prev => ({ ...prev, order_value: e.target.value }))}
                    placeholder="99.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                    data-testid="new-order-value"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (kg) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={newOrderForm.total_weight}
                    onChange={(e) => setNewOrderForm(prev => ({ ...prev, total_weight: e.target.value }))}
                    placeholder="2.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                    data-testid="new-order-weight"
                    required
                  />
                </div>
              </div>
              
              {/* Delivery Time Window (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Time Window <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newOrderForm.delivery_time_window}
                  onChange={(e) => setNewOrderForm(prev => ({ ...prev, delivery_time_window: e.target.value }))}
                  placeholder="9:00 AM - 12:00 PM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-colors"
                  data-testid="new-order-time-window"
                />
              </div>
              
              {/* Submit Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewOrderModal(false)}
                  className="flex-1"
                  data-testid="cancel-new-order"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isCreatingOrder}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="submit-new-order"
                >
                  {isCreatingOrder ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Order
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
